import {
  COMPANION_PANEL_ID,
  focusCompanionComposer,
} from '@/components/companion/feed/companion-panel-ids';
import { useIsMobileLayout } from '@/components/scaffold/layout/nav-dark';
import { SIDEBAR_WIDTH } from '@/components/scaffold/sidebar/PrimarySidebar';
import { useAuth } from '@/contexts/auth-context';
import { isPrimaryMenuShown } from '@/stores/nav-store';
import { companionPanelScope, useCompanionPanelStore } from '@/stores/companion-panel-store';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { decidePanelMode, type PanelMode } from './companion-panel-layout';

export interface OpenCompanionPanel {
  /** Open with a mode: a panel restored open from a previous visit is closed. */
  isOpen: boolean;
  mode: PanelMode | null;
  toggle: () => void;
  close: () => void;
}

/** The width of the menu beside the content, by the same rule as Layout. */
function useSidebarWidth(): number {
  const { pathname } = useLocation();
  const isMobile = useIsMobileLayout();
  return isPrimaryMenuShown(isMobile, pathname) ? SIDEBAR_WIDTH : 0;
}

/**
 * The conversational panel of the current person and brand. Every opening goes
 * through here, so the mode is always decided the same way: from the window
 * and the sidebar Layout shows. `useFollowPanelMode` keeps it current while the
 * panel stays open.
 */
export function useOpenCompanionPanel(): OpenCompanionPanel {
  const { user } = useAuth();
  const scope = companionPanelScope(user?.id ?? '', user?.currentBrandId ?? '');

  const open = useCompanionPanelStore((state) => state.byScope[scope]?.open ?? false);
  const mode = useCompanionPanelStore((state) => state.panelMode);
  const togglePanel = useCompanionPanelStore((state) => state.togglePanel);
  const closePanel = useCompanionPanelStore((state) => state.closePanel);
  const sidebarWidth = useSidebarWidth();

  const toggle = useCallback(
    () => togglePanel(scope, decidePanelMode(window.innerWidth, sidebarWidth)),
    [togglePanel, scope, sidebarWidth]
  );
  const close = useCallback(() => closePanel(scope), [closePanel, scope]);

  const isOpen = open && mode !== null;
  return { isOpen, mode: isOpen ? mode : null, toggle, close };
}

/**
 * While the panel is open, decides its mode again when the window or the
 * sidebar changes. It only moves when a threshold is crossed, so a push never
 * survives a window too narrow to push. Turning full screen over the page
 * brings the keyboard into the panel, since the page behind becomes inert.
 * Called once, by the panel's host.
 */
export function useFollowPanelMode(): void {
  const { isOpen, mode } = useOpenCompanionPanel();
  const setPanelMode = useCompanionPanelStore((state) => state.setPanelMode);
  const sidebarWidth = useSidebarWidth();

  useEffect(() => {
    if (!isOpen) return;
    const follow = () => setPanelMode(decidePanelMode(window.innerWidth, sidebarWidth));
    follow();
    window.addEventListener('resize', follow);
    return () => window.removeEventListener('resize', follow);
  }, [isOpen, sidebarWidth, setPanelMode]);

  const previousModeRef = useRef(mode);
  useEffect(() => {
    const previous = previousModeRef.current;
    previousModeRef.current = mode;
    if (mode !== 'fullscreen' || previous === null || previous === 'fullscreen') return;
    if (!document.activeElement?.closest(`#${COMPANION_PANEL_ID}`)) focusCompanionComposer();
  }, [mode]);
}
